<template>
    <v-dialog
        v-model="dialogModel"
        width="900"
        max-width="95%"
        @click:outside.prevent="closeDialog"
    >
        <v-card>
            <v-card-title class="text-h6 text-md-h5 grey lighten-2">
                <v-row>
                    <v-col cols="10">
                        Health Screening Questionnaire
                    </v-col>
                    <v-col cols="2" class="text-right">
                        <v-btn icon @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text class="py-5" style="max-height: 75vh; overflow-y: auto;">
                <v-form ref="healthScreeningForm" lazy-validation>
                    <h2 class="mb-4">Private and Confidential</h2>
                    <p>
                        This form should only be completed by the employee.
                    </p>
                    <p>
                        The information provided in this form will be used to determine if you require additional assistance at work due to pre-existing medical condidions, and ensure that the working environment will be safe for you.
                    </p>
                    <p>
                        Based on the information provided, we may need to seek advice from a doctor or occupational health specialist. We may also request that you regularly attend health surveillance during your employment if determined necessary by the company risk assessments or medical practitioner.
                    </p>
                    <p>
                        Advice regarding fitness for work will be accessible to management in general terms, however, detailed clinical information will not be revealed without your consent.
                    </p>
                    <p>
                        If further information is required from your doctor or health specialist, this will only be obtained with your written consent.
                    </p>
                    <p>
                        All information you provide is confidential and will be treated with the utmost confidnece, handled in line with the most recent data protection guidelines.
                    </p>
                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.name.firstName"
                                placeholder="First Name"
                                outlined
                                dense
                                :rules="firstNameRules"
                                hint="Forename(s)"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-end">
                            <v-text-field
                                v-model="form.name.lastName"
                                placeholder="Last Name"
                                outlined
                                dense
                                :rules="lastNameRules"
                                hint="Surname"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Address <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.address.line1"
                                placeholder="Address Line 1"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="Street Address"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-end">
                            <v-text-field
                                v-model="form.address.line2"
                                placeholder="Address Line 2"
                                outlined
                                dense
                                hint="Street Address Line 2"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="form.address.city"
                                placeholder="City"
                                outlined dense
                                :rules="[requiredRules]"
                                hint="City"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="form.address.state"
                                placeholder="State / Province"
                                outlined dense
                                :rules="[requiredRules]"
                                hint="State / Province"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="form.address.postalCode"
                                placeholder="Postal / Zip Code"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="Postal / Zip Code"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Tel No <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.telNo"
                                placeholder="Telephone Number"
                                outlined dense
                                :rules="[requiredRules]"
                                type="number"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Email Address<span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.email"
                                placeholder="example@example.com"
                                outlined
                                dense
                                :rules="emailRules"
                                hint="example@example.com"
                                :persistent-hint="true"
                                type="email"
                            />
                        </v-col>
                    </v-row>
                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Name of Doctor Surgery <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.doctorSurgeryName"
                                placeholder="Doctor Surgery Name"
                                outlined
                                dense
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Address of Doctor Surgery<span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.doctorSurgeryAddress.line1"
                                placeholder="Street Address Line 1"
                                outlined dense
                                :rules="[requiredRules]"
                                hint="Street Address"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-end">
                            <v-text-field
                                v-model="form.doctorSurgeryAddress.line2"
                                placeholder="Street Address Line 2"
                                outlined dense
                                hint="Street Address Line 2"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="form.doctorSurgeryAddress.city"
                                placeholder="City"
                                outlined dense
                                :rules="[requiredRules]"
                                hint="City"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="form.doctorSurgeryAddress.state"
                                placeholder="State / Province"
                                outlined dense
                                :rules="[requiredRules]"
                                hint="State / Province"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="form.doctorSurgeryAddress.postalCode"
                                placeholder="Postal / Zip Code"
                                outlined dense
                                :rules="[requiredRules]"
                                hint="Postal / Zip Code"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Job Title <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.jobTitle"
                                placeholder="Job Title"
                                outlined dense
                                :rules="[requiredRules]"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Date of Birth <span class="red--text">*</span></h3>
                            <v-menu v-model="dobMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="form.dateOfBirthFormatted"
                                        placeholder="DD/MM/YYYY"
                                        readonly
                                        outlined
                                        dense
                                        prepend-icon="mdi-calendar"
                                        v-bind="attrs"
                                        v-on="on"
                                        :rules="[requiredRules]"
                                        hint="Date"
                                        :persistent-hint="true"
                                    />
                                </template>
                                <v-date-picker v-model="form.dateOfBirth" :max="today" @input="dobMenu = false" />
                            </v-menu>
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="4">
                            <h3 class="mb-2">Sex <span class="red--text">*</span></h3>
                            <v-radio-group v-model="form.sex" row :rules="[requiredRules]">
                                <v-radio label="Male" value="male" />
                                <v-radio label="Female" value="female" />
                                </v-radio-group>
                        </v-col>
                        <v-col cols="12" md="4">
                            <h3 class="mb-4">Nationality <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nationality" placeholder="Nationality"
                                outlined
                                dense
                                :rules="[requiredRules]"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <h3 class="mb-2">Right to Work in UK <span class="red--text">*</span></h3>
                            <v-radio-group v-model="form.rightToWorkInUk" row :rules="[requiredRules]">
                                <v-radio label="Yes" value="yes" />
                                <v-radio label="No" value="no" />
                            </v-radio-group>
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Next of Kin (1) Name<span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nextOfKin1.firstName"
                                placeholder="First Name"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="First Name"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-end">
                            <v-text-field
                                v-model="form.nextOfKin1.lastName"
                                placeholder="Last Name"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="Last Name"
                                :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>
                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Relationship <span class="red--text">*</span></h3>
                            <v-text-field v-model="form.nextOfKin1.relationship" placeholder="Relationship" outlined dense :rules="[requiredRules]" />
                        </v-col>
                    </v-row>
                    <v-row class="mb-4">
                        <v-col cols="12" md="4">
                            <h3 class="mb-4">Tel No <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nextOfKin1.telNo"
                                placeholder="Telephone Number"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="Telephone Number"
                                :persistent-hint="true"
                                type="number"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <h3 class="mb-4">Home Tel</h3>
                            <v-text-field
                                :value="form.nextOfKin1.homeTel"
                                placeholder="(555) 555-5555"
                                outlined
                                dense
                                inputmode="numeric"
                                maxlength="14"
                                hint="Please enter a valid phone number."
                                :persistent-hint="true"
                                :rules="optionalUsPhoneMaskRules"
                                @keydown="blockNonDigitInput"
                                @input="form.nextOfKin1.homeTel = formatUsPhoneMask($event)"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <h3 class="mb-4">Work Tel</h3>
                            <v-text-field
                                :value="form.nextOfKin1.workTel"
                                placeholder="(555) 555-5555"
                                outlined
                                dense
                                inputmode="numeric"
                                maxlength="14"
                                hint="Please enter a valid phone number."
                                :persistent-hint="true"
                                :rules="optionalUsPhoneMaskRules"
                                @keydown="blockNonDigitInput"
                                @input="form.nextOfKin1.workTel = formatUsPhoneMask($event)"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Next of Kin (2) Name<span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nextOfKin2.firstName"
                                placeholder="First Name"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="First Name"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-end">
                            <v-text-field
                                v-model="form.nextOfKin2.lastName"
                                placeholder="Last Name"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="Last Name"
                                :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Relationship<span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nextOfKin2.relationship"
                                placeholder="Relationship"
                                outlined
                                dense
                                :rules="[requiredRules]"
                            />
                        </v-col>
                    </v-row>
                    <v-row class="mb-4">
                        <v-col cols="12" md="4">
                            <h3 class="mb-4">Tel No <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.nextOfKin2.telNo"
                                placeholder="Telephone Number"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                hint="Telephone Number"
                                :persistent-hint="true"
                                type="number"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <h3 class="mb-4">Home Tel</h3>
                            <v-text-field
                                v-model="form.nextOfKin2.homeTel"
                                placeholder="(555) 555-5555"
                                outlined
                                dense
                                inputmode="numeric"
                                maxlength="14"
                                hint="Please enter a valid phone number."
                                :persistent-hint="true"
                                :rules="optionalUsPhoneMaskRules"
                                @keydown="blockNonDigitInput"
                                @input="form.nextOfKin2.homeTel = formatUsPhoneMask($event)"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <h3 class="mb-4">Work Tel</h3>
                            <v-text-field
                                v-model="form.nextOfKin2.workTel"
                                placeholder="(555) 555-5555"
                                outlined
                                dense
                                inputmode="numeric"
                                maxlength="14"
                                hint="Please enter a valid phone number."
                                :persistent-hint="true"
                                :rules="optionalUsPhoneMaskRules"
                                @keydown="blockNonDigitInput"
                                @input="form.nextOfKin2.workTel = formatUsPhoneMask($event)"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">SECTION C. Job involves</h3>
                            <CheckboxGroupField
                                v-model="form.jobInvolves"
                                :items="jobInvolvesItems"
                                :md-cols="6"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Other Hazards - Please State</h3>
                            <v-text-field
                                v-model="form.otherHazards"
                                placeholder="Please provide details"
                                outlined
                                dense
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Do you have, or have you previously had, any of the following health conditions? Please tick which options apply to you</h3>
                            <CheckboxGroupField
                                v-model="form.healthConditions"
                                :items="healthConditionsItems"
                                :md-cols="6"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">How Many Days have you been absent from work in the last 3 years because of illness or physical injury?</h3>
                            <v-text-field
                                v-model="form.absentDaysLast3Years"
                                placeholder="Example: 23"
                                outlined
                                dense
                                type="number"
                                :rules="nonNegativeOptionalNumberRules"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Currently taking prescribed medication? <span class="red--text">*</span></h3>
                            <v-radio-group v-model="form.takingMedication" row :rules="[requiredRules]">
                                <v-radio label="Yes" value="yes" />
                                <v-radio label="No" value="no" />
                            </v-radio-group>
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">If yes, please state <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.medicationDetails"
                                placeholder="Medication details"
                                outlined
                                dense
                                :rules="[value => form.takingMedication !== 'yes' || !!String(value || '').trim() || 'Required']"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Any known allergies? <span class="red--text">*</span></h3>
                            <v-radio-group v-model="form.hasAllergies" row :rules="[requiredRules]">
                                <v-radio label="Yes" value="yes" />
                                <v-radio label="No" value="no" />
                            </v-radio-group>
                        </v-col>
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">If yes, please state <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.allergyDetails"
                                placeholder="Allergy details"
                                outlined dense
                                :rules="[value => form.hasAllergies !== 'yes' || !!String(value || '').trim() || 'Required']"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Immunisations</h3>
                            <CheckboxGroupField
                                v-model="form.immunisations"
                                :items="immunisationItems"
                                :md-cols="12" />
                        </v-col>
                    </v-row>

                    <v-row dense class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Serum Conversion Check Date</h3>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-menu v-model="serumDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="form.serumConversionCheckDateFormatted"
                                        placeholder="DD/MM/YYYY"
                                        readonly
                                        outlined
                                        dense
                                        prepend-icon="mdi-calendar"
                                        v-bind="attrs"
                                        v-on="on"
                                        hint="Date"
                                        :persistent-hint="true"
                                    />
                                </template>
                                <v-date-picker v-model="form.serumConversionCheckDate" @input="serumDateMenu = false" />
                            </v-menu>
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Do you have any disabilities that affect the following? Please tick which options apply to you</h3>
                            <CheckboxGroupField
                                v-model="form.disabilitiesAffecting"
                                :items="disabilityItems"
                                :md-cols="12"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12">
                            <p>If you have ticked (v) any of the above options, you may be asked to see a doctor or nurse for further assessment.</p>
                            <p>I confirm that to the best of my knowledge and belief, the above information is correct. Iunderstand that any failure to disclose information could lead to a re-assessment of my general fitness, which could ultimately lead to the termination of my employment.</p>
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Name (Block Capitals)<span class="red--text">*</span></h3>
                            <v-text-field
                            v-model="form.declarationName.firstName"
                            placeholder="First Name"
                            outlined
                            dense
                            :rules="firstNameRules"
                            hint="First Name"
                            :persistent-hint="true"
                            />
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-end">
                            <v-text-field
                            v-model="form.declarationName.lastName"
                            placeholder="Last Name"
                            outlined
                            dense
                            :rules="lastNameRules"
                            hint="Last Name"
                            :persistent-hint="true"
                            />
                        </v-col>
                    </v-row>

                    <v-row dense class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Date <span class="red--text">*</span></h3>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="form.dateFormatted"
                                        placeholder="DD-MM-YYYY"
                                        readonly
                                        outlined
                                        dense
                                        prepend-icon="mdi-calendar"
                                        v-bind="attrs"
                                        v-on="on"
                                        :rules="[requiredRules]"
                                    />
                                </template>
                                <v-date-picker v-model="form.date" @input="dateMenu = false" />
                            </v-menu>
                        </v-col>
                    </v-row>

                    <div class="mt-5 pt-5" style="border-top:1px solid #e0e0e0">
                        <div class="d-flex justify-end">
                            <v-btn color="primary" :loading="submitLoading" @click="onSubmit">
                                Submit
                            </v-btn>
                        </div>
                    </div>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import validationRules from '@/common/validationRules'
    import CheckboxGroupField from '@/components/common/CheckboxGroupField.vue'
    import { mapActions } from 'vuex'

    const emptyForm = () => ({
        name: {
            firstName: '',
            lastName: ''
        },
        address: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            postalCode: ''
        },
        telNo: '',
        email: '',
        doctorSurgeryName: '',
        doctorSurgeryAddress: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            postalCode: ''
        },
        jobTitle: '',
        dateOfBirth: null,
        dateOfBirthFormatted: '',
        sex: '',
        nationality: '',
        rightToWorkInUk: '',
        nextOfKin1: {
            firstName: '',
            lastName: '',
            relationship: '',
            telNo: '',
            homeTel: '',
            workTel: ''
        },
        nextOfKin2: {
            firstName: '',
            lastName: '',
            relationship: '',
            telNo: '',
            homeTel: '',
            workTel: ''
        },
        jobInvolves: [],
        otherHazards: '',
        healthConditions: [],
        absentDaysLast3Years: '',
        takingMedication: '',
        medicationDetails: '',
        hasAllergies: '',
        allergyDetails: '',
        immunisations: [],
        serumConversionCheckDate: null,
        serumConversionCheckDateFormatted: '',
        disabilitiesAffecting: [],
        declarationName: {
            firstName: '',
            lastName: ''
        },
        date: null,
        dateFormatted: ''
    })

    export default {
        name: 'HealthScreeningFormDialog',
        components: {
            CheckboxGroupField
        },
        props: ['isOpen', 'programme'],
        data: () => ({
            form: emptyForm(),
            submitLoading: false,
            dobMenu: false,
            serumDateMenu: false,
            dateMenu: false,
            requiredRules: validationRules.required,
            firstNameRules: validationRules.firstName,
            lastNameRules: validationRules.lastName,
            emailRules: validationRules.email,
            requiredCheckboxRule: value => (Array.isArray(value) && value.length > 0) || 'Required',
            nonNegativeOptionalNumberRules: [
                value => {
                    if (value === '' || value === null || value === undefined) {
                        return true
                    }

                    return Number(value) >= 0 || 'Value must be 0 or greater'
                }
            ],
            jobInvolvesItems: [
                { label: 'Regular manual handling/lifting duties', value: 'manual-handling' },
                { label: 'Regular overseas travel', value: 'overseas-travel' },
                { label: 'Human blood, tissues, fluids', value: 'blood-tissues-fluids' },
                { label: 'Regular display screen equipment usage', value: 'dse-usage' },
                { label: 'Respiratory sensitisers or allergens', value: 'respiratory-allergens' },
                { label: 'Latex materials', value: 'latex-materials' },
                { label: 'Use of isocyanate-based products', value: 'isocyanate-products' },
                { label: 'Food handling', value: 'food-handling' },
                { label: 'Regular vehicle driving activities', value: 'vehicle-driving' },
                { label: 'Regular night shifts', value: 'night-shifts' },
                { label: 'Working at height', value: 'working-at-height' },
                { label: 'Lead', value: 'lead' },
                { label: 'Ionising radiations', value: 'ionising-radiations' },
                { label: 'Vibrating equipment', value: 'vibrating-equipment' },
                { label: 'Noisy environments', value: 'noisy-environments' },
                { label: 'Other hazards', value: 'other-hazards' }
            ],
            healthConditionsItems: [
                { label: 'Giddiness, fainting attacks, epilepsy', value: 'giddiness-epilepsy' },
                { label: 'Stroke, heart trouble, high blood pressure or varicose veins', value: 'stroke-heart-bp-varicose' },
                { label: 'Mental illness, anxiety or depression', value: 'mental-illness-anxiety-depression' },
                { label: 'Diabetes', value: 'diabetes' },
                { label: 'Recurring headaches', value: 'recurring-headaches' },
                { label: 'Skin trouble', value: 'skin-trouble' },
                { label: 'Serious injury or operations', value: 'serious-injury-operations' },
                { label: 'Ear trouble or deafness', value: 'ear-trouble-deafness' },
                { label: 'Hay fever, asthma or recurring chest infections', value: 'hay-fever-asthma-chest' },
                { label: 'Colour vision or eye trouble not corrected by glasses or contact lenses', value: 'colour-vision-eye-trouble' },
                { label: 'Recurring stomach or bowel trouble', value: 'stomach-bowel-trouble' },
                { label: 'Back or muscle/joint trouble', value: 'back-muscle-joint-trouble' },
                { label: 'Recurring bladder trouble', value: 'bladder-trouble' },
                { label: 'Hernia or rupture', value: 'hernia-rupture' }
            ],
            immunisationItems: [
                { label: 'Hepatitis', value: 'hepatitis' },
                { label: 'Tetanus', value: 'tetanus' },
                { label: 'Polio', value: 'polio' },
                { label: 'Rubella', value: 'rubella' }
            ],
            disabilityItems: [
                { label: 'Standing', value: 'standing' },
                { label: 'Lifting', value: 'lifting' },
                { label: 'Working at heights', value: 'working-at-heights' },
                { label: 'Walking', value: 'walking' },
                { label: 'Using your hands', value: 'using-hands' },
                { label: 'Climbing ladders', value: 'climbing-ladders' },
                { label: 'Climbing stairs', value: 'climbing-stairs' },
                { label: 'Driving a vehicle', value: 'driving-vehicle' },
                { label: 'Working on staging', value: 'working-on-staging' }
            ],
            optionalUsPhoneMaskRules: [
                (v) => {
                    const s = v != null ? String(v).trim() : ''
                    if (!s) return true
                    const digits = s.replace(/\D/g, '')
                    if (!digits.length) return true
                    return /^\(\d{3}\) \d{3}-\d{4}$/.test(s) || 'Enter a number e.g. (555) 555-5555'
                }
            ]
        }),
        computed: {
            dialogModel: {
                get () {
                    return this.isOpen
                },
                set (value) {
                    this.$emit('update:isOpen', value)
                }
            },
            today () {
                return new Date().toISOString().slice(0, 10)
            }
        },
        watch: {
            'form.date' (val) {
                this.form.dateFormatted = val ? Date.formatDate([val]).trim() : ''
            },
            'form.dateOfBirth' (val) {
                this.form.dateOfBirthFormatted = val ? Date.formatDate([val]).trim() : ''
            },
            'form.serumConversionCheckDate' (val) {
                this.form.serumConversionCheckDateFormatted = val ? Date.formatDate([val]).trim() : ''
            }
        },
        methods: {
            ...mapActions([
                'uploadHealthScreeningAndConfidentialRecordsForm', 'showAlert'
            ]),
            blockNonDigitInput (event) {
                if (event.ctrlKey || event.metaKey || event.altKey) return

                const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End']
                if (allowedKeys.includes(event.key)) return

                if (event.key.length === 1 && !/\d/.test(event.key)) {
                    event.preventDefault()
                }
            },
            formatUsPhoneMask (value) {
                const d = String(value != null ? value : '').replace(/\D/g, '').slice(0, 10)
                if (!d.length) return ''
                if (d.length <= 3) return '(' + d
                if (d.length <= 6) return '(' + d.slice(0, 3) + ') ' + d.slice(3)
                return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6)
            },
            buildPayload () {
                return {
                    transitionProgrammeCourseId: this.programme && this.programme.id ? this.programme.id : 0,
                    firstName: this.form.name.firstName,
                    lastName: this.form.name.lastName,

                    streetAddress: this.form.address.line1,
                    streetAddressLine2: this.form.address.line2,
                    city: this.form.address.city,
                    stateProvince: this.form.address.state,
                    postalZipCode: this.form.address.postalCode,

                    telNo: this.form.telNo,
                    emailAddress: this.form.email,

                    nameOfDoctorSurgery: this.form.doctorSurgeryName,
                    doctorSurgeryStreetAddress: this.form.doctorSurgeryAddress.line1,
                    doctorSurgeryStreetAddressLine2: this.form.doctorSurgeryAddress.line2,
                    doctorSurgeryCity: this.form.doctorSurgeryAddress.city,
                    doctorSurgeryStateProvince: this.form.doctorSurgeryAddress.state,
                    doctorSurgeryPostalZipCode: this.form.doctorSurgeryAddress.postalCode,

                    jobTitle: this.form.jobTitle,
                    dateOfBirth: this.form.dateOfBirth,
                    sex: this.form.sex,
                    nationality: this.form.nationality,
                    rightToWorkInUk: this.form.rightToWorkInUk,

                    nextOfKin1FirstName: this.form.nextOfKin1.firstName,
                    nextOfKin1LastName: this.form.nextOfKin1.lastName,
                    nextOfKin1Relationship: this.form.nextOfKin1.relationship,
                    nextOfKin1TelNo: this.form.nextOfKin1.telNo,
                    nextOfKin1HomeTel: this.form.nextOfKin1.homeTel,
                    nextOfKin1WorkTel: this.form.nextOfKin1.workTel,

                    nextOfKin2FirstName: this.form.nextOfKin2.firstName,
                    nextOfKin2LastName: this.form.nextOfKin2.lastName,
                    nextOfKin2Relationship: this.form.nextOfKin2.relationship,
                    nextOfKin2TelNo: this.form.nextOfKin2.telNo,
                    nextOfKin2HomeTel: this.form.nextOfKin2.homeTel,
                    nextOfKin2WorkTel: this.form.nextOfKin2.workTel,

                    jobInvolvesRegularManualHandlingLiftingDuties: this.form.jobInvolves.includes('manual-handling'),
                    jobInvolvesRegularOverseasTravel: this.form.jobInvolves.includes('overseas-travel'),
                    jobInvolvesHumanBloodTissuesFluids: this.form.jobInvolves.includes('blood-tissues-fluids'),
                    jobInvolvesRegularDisplayScreenEquipmentUsage: this.form.jobInvolves.includes('dse-usage'),
                    jobInvolvesRespiratorySensitisersOrAllergens: this.form.jobInvolves.includes('respiratory-allergens'),
                    jobInvolvesLatexMaterials: this.form.jobInvolves.includes('latex-materials'),
                    jobInvolvesUseOfIsocyanateBasedProducts: this.form.jobInvolves.includes('isocyanate-products'),
                    jobInvolvesFoodHandling: this.form.jobInvolves.includes('food-handling'),
                    jobInvolvesRegularVehicleDrivingActivities: this.form.jobInvolves.includes('vehicle-driving'),
                    jobInvolvesRegularNightShifts: this.form.jobInvolves.includes('night-shifts'),
                    jobInvolvesWorkingAtHeight: this.form.jobInvolves.includes('working-at-height'),
                    jobInvolvesLead: this.form.jobInvolves.includes('lead'),
                    jobInvolvesIonisingRadiations: this.form.jobInvolves.includes('ionising-radiations'),
                    jobInvolvesVibratingEquipment: this.form.jobInvolves.includes('vibrating-equipment'),
                    jobInvolvesNoisyEnvironments: this.form.jobInvolves.includes('noisy-environments'),
                    jobInvolvesOtherHazards: this.form.jobInvolves.includes('other-hazards'),
                    otherHazardsPleaseState: String(this.form.otherHazards || '').trim(),

                    healthConditionGiddinessFaintingAttacksEpilepsy: this.form.healthConditions.includes('giddiness-epilepsy'),
                    healthConditionStrokeHeartTroubleHighBloodPressureOrVaricoseVeins: this.form.healthConditions.includes('stroke-heart-bp-varicose'),
                    healthConditionMentalIllnessAnxietyOrDepression: this.form.healthConditions.includes('mental-illness-anxiety-depression'),
                    healthConditionDiabetes: this.form.healthConditions.includes('diabetes'),
                    healthConditionRecurringHeadaches: this.form.healthConditions.includes('recurring-headaches'),
                    healthConditionSkinTrouble: this.form.healthConditions.includes('skin-trouble'),
                    healthConditionSeriousInjuryOrOperations: this.form.healthConditions.includes('serious-injury-operations'),
                    healthConditionEarTroubleOrDeafness: this.form.healthConditions.includes('ear-trouble-deafness'),
                    healthConditionHayFeverAsthmaOrRecurringChestInfections: this.form.healthConditions.includes('hay-fever-asthma-chest'),
                    healthConditionColourVisionOrEyeTroubleNotCorrectedByGlassesOrContactLenses: this.form.healthConditions.includes('colour-vision-eye-trouble'),
                    healthConditionRecurringStomachOrBowelTrouble: this.form.healthConditions.includes('stomach-bowel-trouble'),
                    healthConditionBackOrMuscleJointTrouble: this.form.healthConditions.includes('back-muscle-joint-trouble'),
                    healthConditionRecurringBladderTrouble: this.form.healthConditions.includes('bladder-trouble'),
                    healthConditionHerniaOrRupture: this.form.healthConditions.includes('hernia-rupture'),

                    daysAbsentFromWorkLast3Years: String(this.form.absentDaysLast3Years || ''),

                    areYouCurrentlyTakingAnyPrescribedMedication: this.form.takingMedication === 'yes',
                    prescribedMedicationDetails: this.form.medicationDetails,

                    doYouHaveAnyKnownAllergies: this.form.hasAllergies === 'yes',
                    knownAllergiesDetails: this.form.allergyDetails,

                    immunisationHepatitis: this.form.immunisations.includes('hepatitis'),
                    immunisationTetanus: this.form.immunisations.includes('tetanus'),
                    immunisationPolio: this.form.immunisations.includes('polio'),
                    immunisationRubella: this.form.immunisations.includes('rubella'),

                    serumConversionCheckDate: this.form.serumConversionCheckDate,

                    disabilityStanding: this.form.disabilitiesAffecting.includes('standing'),
                    disabilityLifting: this.form.disabilitiesAffecting.includes('lifting'),
                    disabilityWorkingAtHeights: this.form.disabilitiesAffecting.includes('working-at-heights'),
                    disabilityWalking: this.form.disabilitiesAffecting.includes('walking'),
                    disabilityUsingYourHands: this.form.disabilitiesAffecting.includes('using-hands'),
                    disabilityClimbingLadders: this.form.disabilitiesAffecting.includes('climbing-ladders'),
                    disabilityClimbingStairs: this.form.disabilitiesAffecting.includes('climbing-stairs'),
                    disabilityDrivingAVehicle: this.form.disabilitiesAffecting.includes('driving-vehicle'),
                    disabilityWorkingOnStaging: this.form.disabilitiesAffecting.includes('working-on-staging'),

                    nameBlockCapitalsFirstName: this.form.declarationName.firstName,
                    nameBlockCapitalsLastName: this.form.declarationName.lastName,
                    date: this.form.date,

                    formType: 'HealthScreeningAndConfidentialRecords'
                }
            },
            resetForm () {
                this.form = emptyForm()
            },
            closeDialog () {
                this.resetForm()
                this.$emit('update:isOpen', false)
            },
            async onSubmit () {
                const formRef = this.$refs.healthScreeningForm
                if (!formRef.validate()) {
                    this.showAlert({
                        text: 'Fill all the required fields please',
                        type: 'error'
                    })
                    return
                }

                this.submitLoading = true
                try {
                    const payload = this.buildPayload()
                    await this.uploadHealthScreeningAndConfidentialRecordsForm(payload)
                    this.$emit('saved')
                    this.closeDialog()
                    if (formRef && formRef.resetValidation) formRef.resetValidation()
                } catch (e) {
                    this.showAlert({
                        text: e.message || 'Error saving form',
                        type: 'error'
                    })
                } finally {
                    this.submitLoading = false
                }
            }
        }
    }
</script>
